import { useState, useMemo, useCallback, useEffect } from 'react';
import { runDotVotingTool } from '../../tools/dotVoting';
import { type AppState, type DotVotingItem } from './types';
import { ItemInput } from './components/ItemInput';
import { VotingView } from './components/VotingView';
import { ResultsView } from './components/ResultsView';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { useToolMode, useToolSettings, type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { DotVotingLive } from '../../components/prioritization/session/liveExperience/liveTools/DotVotingLive';

function DotVotingContent(props: ToolHostProps) {
  const toolMode = useToolMode();
  const { isLiveSession, shouldAutoStart, useWorkspaceIds, importSourceLabel } = useLiveToolSession();
  const isAppMode = toolMode === 'app';
  
  const [state, setState] = useState<AppState>('input');
  const [itemNames, setItemNames] = useState<string[]>([]);
  const [items, setItems] = useState<DotVotingItem[]>([]);
  // Starts from the session's setting (the wizard's "Dots per participant");
  // 5 when the tool runs on its own.
  const toolSettings = useToolSettings();
  const [dotsPerParticipant, setDotsPerParticipant] = useState(toolSettings.dotsPerParticipant);
  const [remainingDots, setRemainingDots] = useState(toolSettings.dotsPerParticipant);
  
  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});

  // Ranking comes from the canonical engine adapter (src/tools/dotVoting.ts):
  // the page collects dots; the engine owns tally ordering and tie-aware ranks.
  const [engineRanks, setEngineRanks] = useState<Map<string, number> | null>(null);
  useEffect(() => {
    if (items.length === 0) {
      setEngineRanks(null);
      return;
    }
    let cancelled = false;
    void runDotVotingTool(
      items.map((item) => ({
        id: item.id,
        label: item.name,
        attributes: { votes: item.votes },
      })),
      null,
      { dots_per_participant: dotsPerParticipant }
    ).then((scoreSet) => {
      if (cancelled) return;
      setEngineRanks(
        new Map(scoreSet.items.map((s) => [s.item_id as string, s.rank as number]))
      );
    });
    return () => {
      cancelled = true;
    };
  }, [items, dotsPerParticipant]);

  // Handler for importing items from workspace
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newNames = toolItems.map(item => item.name);
    setItemNames(prev => [...prev, ...newNames]);

    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach((item, idx) => {
      const workspaceId = item.sourceId ?? item.id;
      const localId = useWorkspaceIds
        ? workspaceId
        : `item-${itemNames.length + idx}`;
      newSourceIdMap[localId] = workspaceId;
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));

    // Auto-start in app / live session — create items directly and skip the intermediate screen
    if (shouldAutoStart) {
      const newItems: DotVotingItem[] = toolItems.map((item, idx) => {
        const workspaceId = item.sourceId ?? item.id;
        return {
          id: useWorkspaceIds ? workspaceId : `item-${itemNames.length + idx}`,
          name: item.name,
          votes: 0,
        };
      });
      setItems(newItems);
      setRemainingDots(dotsPerParticipant);
      setState('voting');
    }
  }, [shouldAutoStart, useWorkspaceIds, itemNames.length, dotsPerParticipant]);
  
  // Get results for saving as session result
  const handleGetResults = useCallback((): ToolResultItem[] => {
    // Sort by votes (highest first)
    const sortedItems = [...items].sort((a, b) => b.votes - a.votes);

    return sortedItems.map((item, idx) => ({
      id: sourceIdMap[item.id] || item.id,
      name: item.name,
      // Tie-aware competition rank from the engine; positional fallback only
      // covers the microtask before the adapter result lands.
      rank: engineRanks?.get(item.id) ?? idx + 1,
      score: item.votes,
      scoreBreakdown: {
        votes: item.votes,
        totalDots: dotsPerParticipant,
      },
      metadata: {
        votes: item.votes,
        totalDots: dotsPerParticipant,
      }
    }));
  }, [items, sourceIdMap, dotsPerParticipant, engineRanks]);

  // Initialize items when starting voting
  const handleStart = () => {
    const newItems: DotVotingItem[] = itemNames.map((name, index) => ({
      id: `item-${index}`,
      name,
      votes: 0,
    }));

    setItems(newItems);
    setRemainingDots(dotsPerParticipant);
    setState('voting');
  };

  // Handle voting (placing a dot)
  const handleVote = (itemId: string) => {
    if (remainingDots === 0) return;

    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, votes: item.votes + 1 } : item
      )
    );
    setRemainingDots((prev) => prev - 1);
  };

  // Handle removing a vote
  const handleRemoveVote = (itemId: string) => {
    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === itemId && item.votes > 0) {
          return { ...item, votes: item.votes - 1 };
        }
        return item;
      })
    );
    setRemainingDots((prev) => prev + 1);
  };

  // Handle finishing voting
  const handleFinish = () => {
    if (remainingDots === 0) {
      setState('results');
    }
  };

  // Reset to start over
  const handleReset = () => {
    setState('input');
    setItemNames([]);
    setItems([]);
    setRemainingDots(dotsPerParticipant);
    setSourceIdMap({});
  };

  // Calculate total dots used
  const totalDotsUsed = useMemo(() => {
    return items.reduce((sum, item) => sum + item.votes, 0);
  }, [items]);

  const allDotsPlaced = state === 'voting' && remainingDots === 0;
  const canSubmitLive = isLiveSession && allDotsPlaced;

  return (
    <ToolWrapper
      toolName="Dot Voting"
      toolDescription="Allocate dots to indicate your preferences"
      onImportItems={handleImportItems}
      onGetResults={canSubmitLive || state === 'results' ? handleGetResults : undefined}
      itemCount={itemNames.length}
      {...props}
    >
      <div className="w-full">
        {/* Main Content */}
        <div>
          {state === 'input' && (
            <>
              {/* App mode - dots-per-participant config (only visible before items arrive) */}
              {isAppMode && itemNames.length === 0 && (
                <div className="max-w-2xl mx-auto">
                  <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
                    <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
                      Configure Dot Voting
                    </h2>
                    <p className="text-[var(--text-sec)] mb-6">
                      Set the number of dots each participant can allocate, then import items.
                    </p>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                        Dots per Participant
                      </label>
                      <input
                        type="number"
                        value={dotsPerParticipant}
                        onChange={(e) => setDotsPerParticipant(Math.max(1, parseInt(e.target.value) || 5))}
                        min="1"
                        max="20"
                        className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-transparent text-lg"
                      />
                      <p className="mt-1 text-sm text-[var(--text-ter)]">
                        Recommended: 3-5 dots per participant
                      </p>
                    </div>
                    <p className="text-sm text-[var(--text-ter)]">
                      {`Import items from your ${importSourceLabel} using the button in the header.`}
                    </p>
                  </div>
                </div>
              )}
              {/* Standalone mode - show ItemInput */}
              {!isAppMode && (
                <ItemInput
                  items={itemNames}
                  dotsPerParticipant={dotsPerParticipant}
                  onItemsChange={setItemNames}
                  onDotsChange={setDotsPerParticipant}
                  onStart={handleStart}
                />
              )}
            </>
          )}

          {state === 'voting' && isLiveSession && (
            <LiveToolScaffold
              title="Dot voting"
              subtitle="Place all your dots on the items that matter most"
              progressDone={dotsPerParticipant - remainingDots}
              progressTotal={dotsPerParticipant}
            >
              <DotVotingLive
                items={items}
                dotsPerParticipant={dotsPerParticipant}
                remainingDots={remainingDots}
                onVote={handleVote}
                onRemoveVote={handleRemoveVote}
              />
            </LiveToolScaffold>
          )}

          {state === 'voting' && !isLiveSession && (
            <VotingView
              items={items}
              dotsPerParticipant={dotsPerParticipant}
              remainingDots={remainingDots}
              onVote={handleVote}
              onRemoveVote={handleRemoveVote}
              onFinish={handleFinish}
            />
          )}

          {state === 'results' && (
            <ResultsView
              items={items}
              dotsPerParticipant={dotsPerParticipant}
              totalDotsUsed={totalDotsUsed}
              onReset={handleReset}
            />
          )}
        </div>
      </div>
    </ToolWrapper>
  );
}

export default function DotVoting(props: ToolHostProps) {
  return <DotVotingContent {...props} />;
}
